import React, { useEffect, useState } from "react"
import { Alert, Button } from "antd"
import { useHistory, useLocation } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarCheck } from "@fortawesome/pro-regular-svg-icons"
import { swimminglyApi } from "../utils"
import { useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../../appData/types"
import { RoleHelper } from "../utils/helperFunctions"

interface NewOnboardingSummaryResponse {
  count?: number
  hasNew?: boolean
  lastVisitedAt?: string | null
}

export default function WebsitesOnboardingNewAlert(): JSX.Element | null {
  const [loading, setLoading] = useState<boolean>(false)
  const [hasNew, setHasNew] = useState<boolean>(false)
  const history = useHistory()
  const location = useLocation()
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const roleHelper = new RoleHelper(user?.role)
  const isSwimminglyAdmin = roleHelper.isSwimminglyAdmin()

  useEffect(() => {
    if (!isSwimminglyAdmin) {
      setHasNew(false)
      setLoading(false)
      return
    }

    let mounted = true

    const loadSummary = async () => {
      setLoading(true)
      try {
        const resp = await swimminglyApi.get(
          "/api/admin/websites/onboarding/new-summary",
        )
        const payload: NewOnboardingSummaryResponse =
          (resp as any)?.data && !Array.isArray(resp)
            ? (resp as any).data
            : (resp as any)

        if (mounted) {
          setHasNew(Boolean(payload?.hasNew))
        }
      } catch {
        if (mounted) {
          setHasNew(false)
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    const handleViewed = () => {
      loadSummary()
    }

    loadSummary()
    window.addEventListener("websitesOnboardingViewed", handleViewed)

    return () => {
      mounted = false
      window.removeEventListener("websitesOnboardingViewed", handleViewed)
    }
  }, [isSwimminglyAdmin])

  // Only Swimmingly Admins should see this alert
  if (!isSwimminglyAdmin) {
    return null
  }

  if (loading || !hasNew) {
    return null
  }

  if (!location.pathname.startsWith("/app")) {
    return null
  }

  const message = "New Websites onboarding session booked"

  const handleClick = async () => {
    try {
      await swimminglyApi.post("/api/admin/websites/onboarding/mark-viewed")
      window.dispatchEvent(new Event("websitesOnboardingViewed"))
    } catch {
      // ignore; best-effort
    }

    history.push("/app/admin/controlpanel?tab=websites-onboarding-scheduler")
  }

  return (
    <div style={{ padding: "0 12px", marginTop: 12 }}>
      <Alert
        type="info"
        showIcon
        icon={<FontAwesomeIcon icon={faCalendarCheck} />}
        title={message}
        action={
          <Button size="small" type="primary" onClick={handleClick}>
            View Booking
          </Button>
        }
        closable={false}
      />
    </div>
  )
}
